import { Button, cn } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Trash2, GripVertical } from "lucide-react";
import type { ChildPage } from "../../../shared/schema";

export interface ChildPageItemProps {
  page: ChildPage;
  isSelected: boolean;
  isDragging: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

// An indented sub-page row nested one level under a top-level PageItem.
// Sub-pages can't have further children, so there's no expand/collapse chevron or "Add sub-page" affordance.
export const ChildPageItem: React.FC<ChildPageItemProps> = ({
  page,
  isSelected,
  isDragging,
  onSelect,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnd,
}) => {
  const { t } = useTranslation();

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      className={cn(
        "group flex items-center gap-1 rounded-md px-2 py-2 text-sm ml-5",
        isSelected ? "bg-primary text-primary-foreground" : "hover:bg-muted",
        isDragging && "opacity-50"
      )}
    >
      <div className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-muted/50 rounded flex-shrink-0">
        <GripVertical
          className={cn("h-3.5 w-3.5", isSelected ? "text-primary-foreground/70" : "text-muted-foreground")}
        />
      </div>

      <div className="flex-1 flex items-center gap-2 cursor-pointer min-w-0" onClick={onSelect}>
        <span className="truncate">{page.title}</span>
        {page.visibility && (
          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded flex-shrink-0">
            {t("Conditional")}
          </span>
        )}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "h-6 w-6 p-0 opacity-0 group-hover:opacity-100 flex-shrink-0",
          isSelected
            ? "hover:bg-primary-foreground/10 text-primary-foreground"
            : "hover:bg-destructive/10 hover:text-destructive"
        )}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
};
